/**
 * Mock API per il sistema fornitori
 *
 * Questo modulo fornisce un sistema completo di mock per le API dei fornitori,
 * con supporto per dati strutturati, paginazione (page/row), ricerca e gestione errori.
 *
 * @example
 * ```typescript
 * import { API_MOCKS, FORNITORI_ENDPOINTS } from '@/mocks/api';
 *
 * // Ottieni lista fornitori con filtri
 * const response = API_MOCKS.FORNITORI.LIST({
 *   search: "Acme",
 *   stato: "ATTIVO",
 *   page: 1,
 *   pageSize: 10
 * });
 *
 * // Ottieni dettaglio fornitore
 * const dettaglio = API_MOCKS.FORNITORI.VIEW(1);
 * ```
 */

// Esporta i tipi principali
export type {
  FornitoreDettagliato,
  FornitoreDettagliatoInput,
  FornitoreBase,
  DatiPrincipali,
  Azienda,
  ReferenteLegale,
  RatingRischio,
  DatiEconomici,
  Monitoraggio,
  CreditoCommerciale,
  CategorieInternalUse,
  Pagamenti,
  Metadati,
  ApiResponse,
  MockResponse,
  LookupData,
  LookupItem,
  MenuItem,
} from "./types";

// Esporta i dati mock
export {
  FORNITORI_DETTAGLIATI,
  FORNITORI_BASE,
  LOOKUP_DATA,
  MENU_ITEMS,
  GRUPPI_FORNITORI,
  type GruppoFornitore,
} from "./fornitori-data";

// Esporta le funzioni mock
export {
  API_MOCKS,
  FORNITORI_MOCK_RESPONSES,
  SISTEMA_MOCK_RESPONSES,
  createDynamicMockResponse,
} from "./fornitori-mock";

// Import per le funzioni locali
import { API_MOCKS } from "./fornitori-mock";
import type { MockResponse, ApiResponse } from "./types";

// Esporta il sistema di persistenza
export { MockPersistence, PersistenceManager } from "./persistence";

/**
 * Endpoints delle API utilizzate nel sistema
 * Questi sono gli stessi definiti in costanti.ts
 */
export const FORNITORI_ENDPOINTS = {
  LIST: "/v1/anagrafica/fornitori/list",
  VIEW: "/v1/anagrafica/fornitori/view",
  ADD: "/v1/anagrafica/fornitori/add",
  EDIT: "/v1/anagrafica/fornitori/edit",
  DELETE: "/v1/anagrafica/fornitori",
  LOOKUP: "/v1/lookup/get",
  MENU: "/v1/sistema/menu/get",
  GRUPPI_FORNITORI_LIST: "/v1/mock/gruppi-fornitori",
  GRUPPI_FORNITORI_VIEW: "/v1/mock/gruppi-fornitori",
  GRUPPI_FORNITORI_ADD: "/v1/mock/gruppi-fornitori",
  GRUPPI_FORNITORI_EDIT: "/v1/mock/gruppi-fornitori",
  GRUPPI_FORNITORI_DELETE: "/v1/mock/gruppi-fornitori",
  GRUPPI_FORNITORI_FORNITORI: "/v1/mock/gruppi-fornitori/{id}/fornitori",
} as const;

/**
 * Configurazione del sistema mock
 */
export interface MockConfiguration {
  enabled: boolean;
  delay: number;
  randomErrors: boolean;
  errorRate: number;
}

/**
 * Configurazione predefinita del sistema mock
 */
export const DEFAULT_MOCK_CONFIG: MockConfiguration = {
  // enabled: process.env.VUE_APP_MOCK_ENABLED === "true",
  enabled: true,
  delay: 500,
  randomErrors: false,
  errorRate: 0.05, // 5% di errori casuali
};

/**
 * Utilità per la gestione della configurazione mock
 */
export class MockConfigManager {
  private static config: MockConfiguration = { ...DEFAULT_MOCK_CONFIG };

  static getConfig(): MockConfiguration {
    return { ...this.config };
  }

  static updateConfig(updates: Partial<MockConfiguration>): void {
    this.config = { ...this.config, ...updates };
  }

  static enableMocks(): void {
    this.config.enabled = true;
  }

  static disableMocks(): void {
    this.config.enabled = false;
  }

  static setDelay(delay: number): void {
    this.config.delay = Math.max(0, delay);
  }

  static enableRandomErrors(errorRate = 0.05): void {
    this.config.randomErrors = true;
    this.config.errorRate = Math.max(0, Math.min(1, errorRate));
  }

  static disableRandomErrors(): void {
    this.config.randomErrors = false;
  }
}

/**
 * Helper per simulare delay di rete
 */
export const simulateNetworkDelay = async (
  customDelay?: number
): Promise<void> => {
  const delay = customDelay ?? MockConfigManager.getConfig().delay;
  return new Promise((resolve) => setTimeout(resolve, delay));
};

/**
 * Helper per simulare errori casuali
 */
export const shouldSimulateError = (): boolean => {
  const config = MockConfigManager.getConfig();
  if (!config.randomErrors) return false;
  return Math.random() < config.errorRate;
};

/**
 * Utility di logging per il sistema mock
 */
export class MockLogger {
  private static isEnabled = process.env.NODE_ENV === "development";

  static log(message: string, data?: unknown): void {
    if (!this.isEnabled) return;
    console.log(`[MOCK] ${message}`, data || "");
  }

  static warn(message: string, data?: unknown): void {
    if (!this.isEnabled) return;
    console.warn(`[MOCK WARNING] ${message}`, data || "");
  }

  static error(message: string, error?: unknown): void {
    if (!this.isEnabled) return;
    console.error(`[MOCK ERROR] ${message}`, error || "");
  }

  static group(label: string, callback: () => void): void {
    if (!this.isEnabled) return;
    console.group(`[MOCK] ${label}`);
    callback();
    console.groupEnd();
  }
}

/**
 * Funzione per ottenere la risposta mock basata su endpoint e metodo
 */
export const getMockResponse = async (
  endpoint: string,
  method: string,
  payload?: Record<string, unknown>
): Promise<MockResponse<ApiResponse<unknown>> | null> => {
  const config = MockConfigManager.getConfig();
  if (!config.enabled) return null;

  // Normalizza l'endpoint rimuovendo query parameters e parametri dinamici
  const cleanEndpoint = endpoint.split("?")[0];

  MockLogger.log(`Checking mock for: ${method.toUpperCase()} ${cleanEndpoint}`);

  // Simula errori casuali se abilitati
  if (shouldSimulateError()) {
    MockLogger.warn("Simulating random error");
    return {
      status: 500,
      data: {
        success: false,
        message: "Errore simulato per testing",
        results: {},
      },
    };
  }

  // Gestione specifica per gli endpoint dei fornitori
  switch (cleanEndpoint) {
    case FORNITORI_ENDPOINTS.LIST:
      if (method.toUpperCase() === "GET") {
        // Estrai parametri dalla query string se presenti
        const urlParams = new URLSearchParams(endpoint.split("?")[1] || "");
        const page = parseInt(urlParams.get("page") || "1");
        const fetchrows = parseInt(urlParams.get("fetchrows") || "25"); // fetchrows = righe per pagina
        const rowstoskip = parseInt(urlParams.get("rowstoskip") || "0"); // rowstoskip = offset

        // Calcola la pagina corrente dal rowstoskip se presente
        const calculatedPage =
          rowstoskip > 0 ? Math.floor(rowstoskip / fetchrows) + 1 : page;

        const params = {
          search: urlParams.get("nome") || urlParams.get("search") || undefined, // Mappiamo 'nome' a 'search'
          stato: urlParams.get("stato") || undefined,
          provincia: urlParams.get("provincia") || undefined,
          tipologia: urlParams.get("tipologia") || undefined,
          page: calculatedPage,
          pageSize: fetchrows, // Mappiamo fetchrows su pageSize per l'API interna
        };

        MockLogger.log(
          `LIST fornitori - Page: ${calculatedPage}, PageSize: ${fetchrows}, RowsToSkip: ${rowstoskip}, Search: "${params.search}"`,
          params
        );
        return API_MOCKS.FORNITORI.LIST(params);
      }
      break;

    case FORNITORI_ENDPOINTS.VIEW:
      if (method.toUpperCase() === "GET") {
        let id = 1; // Default

        // Prova prima con URL parameter (es: /v1/anagrafica/fornitori/view/1)
        const urlIdMatch = endpoint.match(/\/view\/(\d+)/);
        if (urlIdMatch) {
          id = parseInt(urlIdMatch[1]);
        } else {
          // Poi prova con query parameter (es: /v1/anagrafica/fornitori/view?id=1)
          const queryIdMatch = endpoint.match(/\/view\?.*id=(\d+)/);
          if (queryIdMatch) {
            id = parseInt(queryIdMatch[1]);
          }
        }

        MockLogger.log(`VIEW fornitore ID: ${id} (endpoint: ${endpoint})`);
        return API_MOCKS.FORNITORI.VIEW(id);
      }
      break;

    case FORNITORI_ENDPOINTS.ADD:
      if (method.toUpperCase() === "POST") {
        return API_MOCKS.FORNITORI.ADD(payload as any);
      }
      break;

    case FORNITORI_ENDPOINTS.EDIT:
      if (method.toUpperCase() === "PUT") {
        // Estrai ID dall'endpoint
        const idMatch = endpoint.match(/\/edit\/(\d+)/);
        const id = idMatch ? parseInt(idMatch[1]) : 1;
        return API_MOCKS.FORNITORI.EDIT(id, payload as any);
      }
      break;

    case FORNITORI_ENDPOINTS.LOOKUP:
      if (method.toUpperCase() === "POST") {
        const types = payload?.types as string[];
        return API_MOCKS.SISTEMA.LOOKUP(types);
      }
      break;

    case FORNITORI_ENDPOINTS.MENU:
      if (method.toUpperCase() === "GET") {
        return API_MOCKS.SISTEMA.MENU();
      }
      break;

    // Gruppi Fornitori - LIST
    case FORNITORI_ENDPOINTS.GRUPPI_FORNITORI_LIST:
      if (method.toUpperCase() === "GET") {
        const urlParams = new URLSearchParams(endpoint.split("?")[1] || "");
        const page = parseInt(urlParams.get("page") || "1");
        const fetchrows = parseInt(urlParams.get("fetchrows") || "25");
        const rowstoskip = parseInt(urlParams.get("rowstoskip") || "0");
        const calculatedPage =
          rowstoskip > 0 ? Math.floor(rowstoskip / fetchrows) + 1 : page;

        const params = {
          search: urlParams.get("nome") || urlParams.get("search") || undefined,
          page: calculatedPage,
          pageSize: fetchrows,
        };

        MockLogger.log(
          `LIST gruppi - Page: ${calculatedPage}, Search: "${params.search}"`
        );
        return API_MOCKS.GRUPPI_FORNITORI.LIST(params);
      }
      break;

    // Gruppi Fornitori - VIEW
    case FORNITORI_ENDPOINTS.GRUPPI_FORNITORI_VIEW:
      if (method.toUpperCase() === "GET") {
        let id = 1;
        const urlIdMatch = endpoint.match(/\/gruppi-fornitori\/(\d+)/);
        if (urlIdMatch) {
          id = parseInt(urlIdMatch[1]);
        } else {
          const queryIdMatch = endpoint.match(/\/gruppi-fornitori\?.*id=(\d+)/);
          if (queryIdMatch) {
            id = parseInt(queryIdMatch[1]);
          }
        }
        MockLogger.log(`VIEW gruppo ID: ${id}`);
        return API_MOCKS.GRUPPI_FORNITORI.VIEW(id);
      }
      break;

    // Gruppi Fornitori - ADD
    case FORNITORI_ENDPOINTS.GRUPPI_FORNITORI_ADD:
      if (method.toUpperCase() === "POST") {
        return API_MOCKS.GRUPPI_FORNITORI.ADD(payload as any);
      }
      break;

    // Gruppi Fornitori - EDIT
    case FORNITORI_ENDPOINTS.GRUPPI_FORNITORI_EDIT:
      if (method.toUpperCase() === "PUT") {
        const idMatch = endpoint.match(/\/gruppi-fornitori\/(\d+)/);
        const id = idMatch ? parseInt(idMatch[1]) : 1;
        return API_MOCKS.GRUPPI_FORNITORI.EDIT(id, payload as any);
      }
      break;

    default:
    // // Gestione delete dinamico fornitori
    // if (
    //   cleanEndpoint.startsWith("/v1/anagrafica/fornitori") &&
    //   method.toUpperCase() === "DELETE"
    // ) {
    //   const idMatch = endpoint.match(/\/(\d+)$/);
    //   const id = idMatch ? parseInt(idMatch[1]) : 1;
    //   return API_MOCKS.FORNITORI.DELETE(id);
    // }

    // // Gestione VIEW singolo gruppo fornitore (GET con ID)
    // if (
    //   cleanEndpoint.match(/\/v1\/gruppi-fornitori\/\d+$/) &&
    //   method.toUpperCase() === "GET"
    // ) {
    //   const idMatch = endpoint.match(/\/gruppi-fornitori\/(\d+)$/);
    //   const id = idMatch ? parseInt(idMatch[1]) : 1;
    //   return API_MOCKS.GRUPPI_FORNITORI.VIEW(id);
    // }

    // // Gestione EDIT gruppi fornitori (PUT con ID)
    // if (
    //   cleanEndpoint.match(/\/v1\/gruppi-fornitori\/\d+$/) &&
    //   method.toUpperCase() === "PUT"
    // ) {
    //   const idMatch = endpoint.match(/\/gruppi-fornitori\/(\d+)$/);
    //   const id = idMatch ? parseInt(idMatch[1]) : 1;
    //   return API_MOCKS.GRUPPI_FORNITORI.EDIT(id, payload as any);
    // }

    // // Gestione delete dinamico gruppi fornitori
    // if (
    //   cleanEndpoint.startsWith("/v1/gruppi-fornitori") &&
    //   method.toUpperCase() === "DELETE"
    // ) {
    //   const idMatch = endpoint.match(/\/gruppi-fornitori\/(\d+)$/);
    //   const id = idMatch ? parseInt(idMatch[1]) : 1;
    //   return API_MOCKS.GRUPPI_FORNITORI.DELETE(id);
    // }

    // // Gestione fornitori di un gruppo - GET
    // if (
    //   cleanEndpoint.match(/\/v1\/gruppi-fornitori\/\d+\/fornitori$/) &&
    //   method.toUpperCase() === "GET"
    // ) {
    //   const idMatch = cleanEndpoint.match(
    //     /\/gruppi-fornitori\/(\d+)\/fornitori/
    //   );
    //   const idGruppo = idMatch ? parseInt(idMatch[1]) : 1;
    //   MockLogger.log(`GET fornitori del gruppo ${idGruppo}`);
    //   return API_MOCKS.GRUPPI_FORNITORI.FORNITORI(idGruppo);
    // }

    // // Gestione aggiunta fornitore a gruppo - POST
    // if (
    //   cleanEndpoint.match(/\/v1\/gruppi-fornitori\/\d+\/fornitori$/) &&
    //   method.toUpperCase() === "POST"
    // ) {
    //   const idMatch = cleanEndpoint.match(
    //     /\/gruppi-fornitori\/(\d+)\/fornitori/
    //   );
    //   const idGruppo = idMatch ? parseInt(idMatch[1]) : 1;
    //   const idFornitore = (payload as any)?.idFornitore || 1;
    //   MockLogger.log(
    //     `AGGIUNGI fornitore ${idFornitore} al gruppo ${idGruppo}`
    //   );
    //   return API_MOCKS.GRUPPI_FORNITORI.AGGIUNGI_FORNITORE(
    //     idGruppo,
    //     idFornitore
    //   );
    // }

    // // Gestione rimozione fornitore da gruppo - DELETE
    // if (
    //   cleanEndpoint.match(/\/v1\/gruppi-fornitori\/\d+\/fornitori\/\d+$/) &&
    //   method.toUpperCase() === "DELETE"
    // ) {
    //   const matches = cleanEndpoint.match(
    //     /\/gruppi-fornitori\/(\d+)\/fornitori\/(\d+)/
    //   );
    //   const idGruppo = matches ? parseInt(matches[1]) : 1;
    //   const idFornitore = matches ? parseInt(matches[2]) : 1;
    //   MockLogger.log(
    //     `RIMUOVI fornitore ${idFornitore} dal gruppo ${idGruppo}`
    //   );
    //   return API_MOCKS.GRUPPI_FORNITORI.RIMUOVI_FORNITORE(
    //     idGruppo,
    //     idFornitore
    //   );
    // }

    // // Gestione gruppi di un fornitore - GET
    // if (
    //   cleanEndpoint.match(/\/v1\/fornitori\/\d+\/gruppi$/) &&
    //   method.toUpperCase() === "GET"
    // ) {
    //   const idMatch = cleanEndpoint.match(/\/fornitori\/(\d+)\/gruppi/);
    //   const idFornitore = idMatch ? parseInt(idMatch[1]) : 1;
    //   MockLogger.log(`GET gruppi del fornitore ${idFornitore}`);
    //   return API_MOCKS.FORNITORI_GRUPPI.getGruppiFornitore(idFornitore);
    // }

    // // Gestione aggiornamento gruppi di un fornitore - PUT
    // if (
    //   cleanEndpoint.match(/\/v1\/fornitori\/\d+\/gruppi$/) &&
    //   method.toUpperCase() === "PUT"
    // ) {
    //   const idMatch = cleanEndpoint.match(/\/fornitori\/(\d+)\/gruppi/);
    //   const idFornitore = idMatch ? parseInt(idMatch[1]) : 1;
    //   const gruppiIds = (payload as any)?.gruppiIds || [];
    //   MockLogger.log(`UPDATE gruppi del fornitore ${idFornitore}`, gruppiIds);
    //   return API_MOCKS.FORNITORI_GRUPPI.updateGruppiFornitore(
    //     idFornitore,
    //     gruppiIds
    //   );
    // }
    // break;
  }

  MockLogger.warn(
    `No mock found for: ${method.toUpperCase()} ${cleanEndpoint}`
  );
  return null;
};

/**
 * Funzione helper comune per gestire le risposte mock
 */
export const handleMockResponse = async (
  endpoint: string,
  method: string,
  payload?: Record<string, unknown>
): Promise<{
  status: number;
  data: unknown;
  headers: Record<string, string>;
} | null> => {
  const mockResponse = await getMockResponse(endpoint, method, payload);
  if (!mockResponse) return null;

  await simulateNetworkDelay();

  return {
    status: mockResponse.status,
    data: mockResponse.data,
    headers: mockResponse.headers || {
      "Content-Type": "application/json",
      "X-Mock-Response": "true",
    },
  };
};

/**
 * Esporta tutto per un facile utilizzo
 */
export default {
  FORNITORI_ENDPOINTS,
  MockConfigManager,
  MockLogger,
  simulateNetworkDelay,
  shouldSimulateError,
  getMockResponse,
  handleMockResponse,
};
